import axios from 'axios';
import { getAuth0Client } from './getAuth0Client';
import { auth0ProviderForSubject } from './auth0Provider';
import {
  ParseSessionError,
  PARSE_SESSION_ERROR_KIND,
} from './parseSessionError';
import {
  isOfcAccess,
  isOfcAccessEnded,
  isOfcAccessExpired,
  markOfcAccessEnded,
} from './ofcAccess';
import { OFC_ACCESS_PAGES, renderOfcAccessPage } from './ofcAccessPage';

export const PARSE_SESSION_STORAGE_KEY = 'parseSessionToken';

const LOGOUT_TIMEOUT_MS = 5000;
const REQUEST_TIMEOUT_MS = 30000;
const PENDING_REVOCATION_KEY = 'parsePendingRevocation';

// Parse error codes, mirrored from Parse-SDK-JS. Kept local to avoid pulling
// the whole SDK into the editor bundle.
const PARSE_ERROR = Object.freeze({
  OBJECT_NOT_FOUND: 101,
  OPERATION_FORBIDDEN: 119,
  DUPLICATE_VALUE: 137,
  VALIDATION_ERROR: 142,
  INVALID_SESSION_TOKEN: 209,
});

const LINK_ERROR_KIND = Object.freeze({
  NO_ACCOUNT: PARSE_SESSION_ERROR_KIND.UNKNOWN_IDENTITY,
  ACCOUNT_CONFLICT: PARSE_SESSION_ERROR_KIND.ACCOUNT_CONFLICT,
  BINDING_CONFLICT: PARSE_SESSION_ERROR_KIND.BINDING_CONFLICT,
  INVALID_REQUEST: PARSE_SESSION_ERROR_KIND.INVALID_CREDENTIALS,
});

let loginPromise;
let logoutPromise;
let ofcAccessEndPromise;
let sessionGeneration = 0;

export const invalidatePendingSession = () => {
  sessionGeneration += 1;
};

const parseServerUrl = (path) => {
  const endpoint = process.env.PARSE_SERVER_ENDPOINT?.replace(/\/+$/, '');
  if (!endpoint) {
    throw new ParseSessionError('Parse Server endpoint is not configured', {
      kind: PARSE_SESSION_ERROR_KIND.CONFIGURATION,
    });
  }

  return `${endpoint}${path}`;
};

const parseHeaders = (sessionToken) => ({
  'content-type': 'application/json',
  'x-parse-application-id': process.env.PARSE_APPLICATION_ID,
  ...(sessionToken ? { 'x-parse-session-token': sessionToken } : {}),
});

const parseErrorCode = (error) => {
  const code = Number(error?.response?.data?.code);
  return Number.isFinite(code) ? code : undefined;
};

const parseErrorName = (error) => {
  const name = error?.response?.data?.error;
  return typeof name === 'string' ? name : undefined;
};

const isTransientError = (error) => {
  if (!error?.response) {
    // No HTTP response: DNS/socket/timeout failures.
    return true;
  }

  const status = Number(error.response.status);
  return Number.isFinite(status) && status >= 500;
};

const toSessionError = (message, error) => {
  if (error instanceof ParseSessionError) {
    return error;
  }

  return new ParseSessionError(message, {
    kind: isTransientError(error)
      ? PARSE_SESSION_ERROR_KIND.TRANSIENT
      : PARSE_SESSION_ERROR_KIND.UNEXPECTED,
    code: parseErrorCode(error),
    status: Number(error?.response?.status) || undefined,
  });
};

const revokeToken = async (token) => {
  try {
    // axios serializes a `null` body to the string "null" when the content
    // type is JSON, and Parse's strict body parser rejects it with a 400 that
    // carries no Parse code. Send an empty object instead.
    await axios.post(
      parseServerUrl('/logout'),
      {},
      {
        headers: parseHeaders(token),
        timeout: LOGOUT_TIMEOUT_MS,
      }
    );
  } catch (error) {
    if (parseErrorCode(error) !== PARSE_ERROR.INVALID_SESSION_TOKEN) {
      throw toSessionError('Session revocation failed', error);
    }
  }
};

export const classifyParseLoginError = (error) => {
  const code = parseErrorCode(error);

  if (code === PARSE_ERROR.OBJECT_NOT_FOUND) {
    // Parse rejects an authData login without a bound account here because
    // `_User.create` requires authentication. It is the only reliable
    // "missing binding" signal; 119 also covers invalid tokens and CLP.
    return PARSE_SESSION_ERROR_KIND.UNKNOWN_IDENTITY;
  }
  if (code === PARSE_ERROR.OPERATION_FORBIDDEN) {
    const message = parseErrorName(error);
    // Only known setup/create-policy refusals may enter controlled invitation
    // linking. The Auth0 adapter emits its generic authentication failure after
    // a valid login token reaches validateSetUp without an existing binding;
    // linkAuth0 independently verifies the ID token before claiming an account.
    // An unrelated 119 is never a missing-binding signal.
    if (
      message === 'Auth0 authentication failed' ||
      message === 'Permission denied for action create on class _User.' ||
      /^_User\.authData\.(auth0|auth0Google|auth0Apple) can only be set by the backend$/.test(
        message || ''
      )
    ) {
      return PARSE_SESSION_ERROR_KIND.UNKNOWN_IDENTITY;
    }
    return PARSE_SESSION_ERROR_KIND.INVALID_CREDENTIALS;
  }
  if (code === PARSE_ERROR.INVALID_SESSION_TOKEN) {
    return PARSE_SESSION_ERROR_KIND.INVALID_SESSION;
  }
  if (isTransientError(error)) {
    return PARSE_SESSION_ERROR_KIND.TRANSIENT;
  }

  return PARSE_SESSION_ERROR_KIND.UNEXPECTED;
};

export const classifyLinkAuth0Error = (error) => {
  const name = parseErrorName(error);
  if (name && LINK_ERROR_KIND[name]) {
    return LINK_ERROR_KIND[name];
  }

  const code = parseErrorCode(error);
  if (code === PARSE_ERROR.OBJECT_NOT_FOUND) {
    return PARSE_SESSION_ERROR_KIND.UNKNOWN_IDENTITY;
  }
  if (code === PARSE_ERROR.DUPLICATE_VALUE) {
    return PARSE_SESSION_ERROR_KIND.ACCOUNT_CONFLICT;
  }
  if (code === PARSE_ERROR.OPERATION_FORBIDDEN) {
    return PARSE_SESSION_ERROR_KIND.BINDING_CONFLICT;
  }
  if (code === PARSE_ERROR.VALIDATION_ERROR) {
    return PARSE_SESSION_ERROR_KIND.INVALID_CREDENTIALS;
  }
  if (isTransientError(error)) {
    return PARSE_SESSION_ERROR_KIND.TRANSIENT;
  }

  return PARSE_SESSION_ERROR_KIND.UNEXPECTED;
};

const getAuth0Credentials = async () => {
  const auth0 = await getAuth0Client();

  // Wait for the token renewal before reading the identity claims. With
  // auth0-spa-js 1.x a renewal rewrites the token cache: a concurrent
  // `getIdTokenClaims` can observe a missing or stale entry and report an
  // absent subject. Both calls default to the client's configured audience and
  // scope, so the claims read after the renewal belong to the same cache entry
  // as the returned access token.
  const accessToken = await auth0.getTokenSilently();
  const idTokenClaims = await auth0.getIdTokenClaims();

  const subject = idTokenClaims?.sub;
  const idToken = idTokenClaims?.__raw;

  if (!accessToken || !subject || !idToken) {
    // Absent credentials are a temporary Auth0 cache/session condition, not an
    // account verdict: the caller must restart the authorization instead of
    // showing a business rejection.
    throw new ParseSessionError('Auth0 credentials are not available', {
      kind: PARSE_SESSION_ERROR_KIND.AUTH0_SESSION,
    });
  }

  let provider;
  try {
    provider = auth0ProviderForSubject(subject);
  } catch (error) {
    throw new ParseSessionError('Unsupported Auth0 identity', {
      kind: PARSE_SESSION_ERROR_KIND.UNSUPPORTED_IDENTITY,
    });
  }

  // Web Giotto logs into Parse with the Auth0 access token: the adapter accepts
  // it when its audience is the Auth0 Management API and `azp` is a web client
  // id. The ID token is only used by linkAuth0, which validates identity claims
  // and the email server side. Do not send the access token to linkAuth0, and do
  // not assume a mobile client id audience.
  return { accessToken, idToken, subject, provider };
};

const requestParseLogin = async ({ provider, subject, accessToken }) => {
  let response;
  try {
    response = await axios.post(
      parseServerUrl('/users'),
      { authData: { [provider]: { id: subject, token: accessToken } } },
      { headers: parseHeaders(), timeout: REQUEST_TIMEOUT_MS }
    );
  } catch (error) {
    throw new ParseSessionError('Parse login failed', {
      kind: classifyParseLoginError(error),
      code: parseErrorCode(error),
      status: Number(error?.response?.status) || undefined,
    });
  }

  if (!response.data?.sessionToken) {
    throw new ParseSessionError('Parse login returned no session', {
      kind: PARSE_SESSION_ERROR_KIND.INVALID_SESSION,
    });
  }

  return response.data.sessionToken;
};

const requestIdentityLink = async ({ idToken }) => {
  let response;
  try {
    response = await axios.post(
      parseServerUrl('/functions/linkAuth0'),
      { idToken },
      { headers: parseHeaders(), timeout: REQUEST_TIMEOUT_MS }
    );
  } catch (error) {
    throw new ParseSessionError('Identity link failed', {
      kind: classifyLinkAuth0Error(error),
      code: parseErrorCode(error),
      status: Number(error?.response?.status) || undefined,
    });
  }

  const result = response.data?.result;
  const status = result?.status;
  if (status === 'denied' && result.reason === 'NO_ACCOUNT') {
    throw new ParseSessionError('No account for this identity', {
      kind: PARSE_SESSION_ERROR_KIND.UNKNOWN_IDENTITY,
    });
  }
  if (status !== 'linked' && status !== 'already_linked') {
    throw new ParseSessionError('Unexpected identity link result', {
      kind: PARSE_SESSION_ERROR_KIND.UNEXPECTED,
    });
  }

  return status;
};

const performParseLogin = async () => {
  const generation = sessionGeneration;
  const pendingRevocation = localStorage.getItem(PENDING_REVOCATION_KEY);
  if (pendingRevocation) {
    await revokeToken(pendingRevocation);
    localStorage.removeItem(PENDING_REVOCATION_KEY);
  }
  const credentials = await getAuth0Credentials();
  let sessionToken;

  try {
    sessionToken = await requestParseLogin(credentials);
  } catch (error) {
    // Only an unbound identity may be linked, and only once. A rejected Auth0
    // token (119), a CLP denial or a transient failure must never trigger a
    // privileged binding attempt.
    if (
      !(error instanceof ParseSessionError) ||
      error.kind !== PARSE_SESSION_ERROR_KIND.UNKNOWN_IDENTITY
    ) {
      throw error;
    }

    await requestIdentityLink(credentials);
    sessionToken = await requestParseLogin(credentials);
  }

  if (generation !== sessionGeneration) {
    localStorage.setItem(PENDING_REVOCATION_KEY, sessionToken);
    await revokeToken(sessionToken);
    localStorage.removeItem(PENDING_REVOCATION_KEY);
    throw new ParseSessionError('Session operation cancelled');
  }
  localStorage.setItem(PARSE_SESSION_STORAGE_KEY, sessionToken);
  return sessionToken;
};

export const getStoredParseSessionToken = () =>
  localStorage.getItem(PARSE_SESSION_STORAGE_KEY);

export const clearParseSession = () => {
  localStorage.removeItem(PARSE_SESSION_STORAGE_KEY);
};

export const storeParseSession = (sessionToken) => {
  localStorage.setItem(PARSE_SESSION_STORAGE_KEY, sessionToken);
};

// Hand the stored token over to the next logout when it cannot be revoked
// now, so that a new session never depends on revoking the previous one.
export const deferParseSessionRevocation = () => {
  const sessionToken = getStoredParseSessionToken();
  if (sessionToken && !localStorage.getItem(PENDING_REVOCATION_KEY)) {
    localStorage.setItem(PENDING_REVOCATION_KEY, sessionToken);
  }
  clearParseSession();
};

const ofcAccessEndedError = () =>
  new ParseSessionError('Argo access ended', {
    kind: PARSE_SESSION_ERROR_KIND.OFC_ACCESS_ENDED,
  });

const performOfcAccessEnd = async () => {
  const exitUrl = markOfcAccessEnded();
  invalidatePendingSession();

  // Best effort: an expired or closed session is already invalid server side
  // (revokeToken ignores 209), and the operator must reach Argo regardless.
  const tokens = [
    localStorage.getItem(PENDING_REVOCATION_KEY),
    getStoredParseSessionToken(),
  ].filter(Boolean);
  await Promise.all(tokens.map((token) => revokeToken(token).catch(() => {})));

  // Like the logout: nothing of the service account survives in the browser.
  localStorage.clear();
  renderOfcAccessPage(OFC_ACCESS_PAGES.SESSION_ENDED, exitUrl);
};

/**
 * Ends the Argo service-account access (PE-2215) where the Auth0 flow would
 * renew the session or ask for a login: the session is cleared and the
 * operator lands on «Sessione terminata», with the way back to Argo. Always
 * rejects with an OFC_ACCESS_ENDED error, so callers stop right there.
 */
export const endOfcAccessSession = async () => {
  if (!ofcAccessEndPromise) ofcAccessEndPromise = performOfcAccessEnd();
  await ofcAccessEndPromise;
  throw ofcAccessEndedError();
};

export const assertOfcAccessActive = async () => {
  if (isOfcAccess() && isOfcAccessExpired()) await endOfcAccessSession();
};

// The Parse session of the service account, used as the API credential.
export const getOfcAccessSessionToken = async () => {
  await assertOfcAccessActive();
  const sessionToken = getStoredParseSessionToken();
  if (!sessionToken) await endOfcAccessSession();
  return sessionToken;
};

export const loginWithAuth0ToParse = async () => {
  // A service-account session can only come from Argo, never from Auth0.
  if (isOfcAccess()) await endOfcAccessSession();
  if (logoutPromise) throw new ParseSessionError('Logout in progress');
  if (!loginPromise) {
    loginPromise = performParseLogin().finally(() => {
      loginPromise = null;
    });
  }

  return loginPromise;
};

const requestCurrentUser = (sessionToken) =>
  axios.get(parseServerUrl('/users/me'), {
    headers: parseHeaders(sessionToken),
    timeout: REQUEST_TIMEOUT_MS,
  });

const wrapCurrentUserError = (error) => {
  const code = parseErrorCode(error);

  if (code === PARSE_ERROR.INVALID_SESSION_TOKEN) {
    return new ParseSessionError('Parse session expired', {
      kind: PARSE_SESSION_ERROR_KIND.INVALID_SESSION,
      code,
    });
  }

  if (
    code === PARSE_ERROR.OPERATION_FORBIDDEN ||
    code === PARSE_ERROR.OBJECT_NOT_FOUND
  ) {
    return new ParseSessionError('Parse session rejected', {
      kind: PARSE_SESSION_ERROR_KIND.INVALID_CREDENTIALS,
      code,
    });
  }

  return toSessionError('Parse session lookup failed', error);
};

// Service-account mode: the Parse session from Argo is the only credential.
// When it is missing, expired or rejected as invalid (209) there is nothing to
// renew it with, so the access ends instead of falling back to Auth0.
const getOfcAccessParseUser = async () => {
  const generation = sessionGeneration;
  const sessionToken = await getOfcAccessSessionToken();

  try {
    const response = await requestCurrentUser(sessionToken);
    return { sessionToken, user: response.data };
  } catch (error) {
    // A concurrent lookup may have ended the access meanwhile.
    if (isOfcAccessEnded()) return endOfcAccessSession();
    if (generation !== sessionGeneration)
      throw new ParseSessionError('Session operation cancelled');
    if (parseErrorCode(error) === PARSE_ERROR.INVALID_SESSION_TOKEN) {
      return endOfcAccessSession();
    }
    throw wrapCurrentUserError(error);
  }
};

export const getCurrentParseUser = async () => {
  if (logoutPromise) throw new ParseSessionError('Logout in progress');
  if (isOfcAccess()) return getOfcAccessParseUser();
  const generation = sessionGeneration;
  let sessionToken = getStoredParseSessionToken();
  if (!sessionToken) {
    sessionToken = await loginWithAuth0ToParse();
  }

  try {
    const response = await requestCurrentUser(sessionToken);
    return { sessionToken, user: response.data };
  } catch (error) {
    if (generation !== sessionGeneration)
      throw new ParseSessionError('Session operation cancelled');
    if (parseErrorCode(error) !== PARSE_ERROR.INVALID_SESSION_TOKEN) {
      throw wrapCurrentUserError(error);
    }

    clearParseSession();
    sessionToken = await loginWithAuth0ToParse();

    try {
      const response = await requestCurrentUser(sessionToken);
      return { sessionToken, user: response.data };
    } catch (retryError) {
      throw wrapCurrentUserError(retryError);
    }
  }
};

const performLogout = async () => {
  invalidatePendingSession();
  if (loginPromise) await loginPromise.catch(() => {});
  const pending = localStorage.getItem(PENDING_REVOCATION_KEY);
  if (pending) {
    await revokeToken(pending);
    localStorage.removeItem(PENDING_REVOCATION_KEY);
  }
  const sessionToken = getStoredParseSessionToken();

  if (!sessionToken) {
    clearParseSession();
    return;
  }

  await revokeToken(sessionToken);
  clearParseSession();
};

export const logoutParseSession = () => {
  if (!logoutPromise) {
    logoutPromise = performLogout().finally(() => {
      logoutPromise = null;
    });
  }
  return logoutPromise;
};
